<template>
  <div class="upload-nca-key-modal" style="width: 500px;">
    <q-form @submit="onSubmit" class="form">
      <div class="form__item" v-if="!providerId">
        <CustomSelect
          :options="providers"
          label="Поставщик"
          optionValueKey="provider_id"
          optionLabelKey="provider_name"
          placeholder="Выберите поставщика"
          :required="true"
          :value="formData.provider_id"
          @change="(value) => onChange('provider_id', value)"
        />
      </div>

      <div class="form__item">
        <CustomInput
          label="Название ключа"
          placeholder="Например: ЮЛ, Доверенность, Бухгалтер"
          :required="true"
          :value="formData.key_alias"
          @change_input="(value) => onChange('key_alias', value)"
        />
      </div>

      <div class="form__item">
        <label class="form__label">
          Файл ключа (.p12) <span class="required">*</span>
        </label>
        <input
          ref="fileInput"
          type="file"
          accept=".p12"
          @change="onFileChange"
          class="file-input"
        />
        <div v-if="fileName" class="file-selected">
          <q-icon name="attach_file" size="16px" />
          <span>{{ fileName }}</span>
        </div>
      </div>

      <div class="form__item">
        <CustomInput
          label="Пароль к ключу"
          placeholder="Введите пароль"
          type="password"
          :required="true"
          :value="formData.password"
          @change_input="(value) => onChange('password', value)"
          :showClearIcon="false"
        />
      </div>

      <div class="info-block">
        <q-icon name="info" size="20px" color="primary" />
        <div class="info-text">
          <p>Ключ и пароль будут зашифрованы с использованием AES-256 перед сохранением в базе данных.</p>
          <p>Никакие данные не хранятся в открытом виде.</p>
        </div>
      </div>

      <CustomModalFooter :btn-loading="loading" />
    </q-form>
  </div>
</template>

<script>
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from "@/services/store/app";
import { providerNcaKeyStoreAPI } from "@/services/api/providerNcaKeyStoreApi";

export default {
  name: "upload-provider-nca-key",
  components: { CustomInput, CustomSelect, CustomModalFooter },
  props: ["data"],
  data() {
    return {
      loading: false,
      fileName: "",
      providers: [],
      formData: {
        provider_id: this.data?.data?.provider_id || "",
        key_alias: "",
        password: "",
        key_file: null,
      },
    };
  },
  computed: {
    providerId() {
      return this.data?.data?.provider_id;
    },
  },
  methods: {
    onChange(key, value) {
      this.formData[key] = value;
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        if (!file.name.toLowerCase().endsWith(".p12")) {
          this.showErrorSnackbar("Поддерживаются только файлы .p12");
          event.target.value = "";
          return;
        }
        this.formData.key_file = file;
        this.fileName = file.name;
      }
    },
    async onSubmit() {
      if (!this.formData.key_file) {
        this.showErrorSnackbar("Выберите файл ключа");
        return;
      }

      if (!this.formData.key_alias.trim()) {
        this.showErrorSnackbar("Введите название ключа");
        return;
      }

      if (!this.formData.password.trim()) {
        this.showErrorSnackbar("Введите пароль");
        return;
      }

      const provider_id = this.providerId || this.formData.provider_id;
      
      if (!provider_id) {
        this.showErrorSnackbar("Выберите поставщика");
        return;
      }

      this.loading = true;

      try {
        const formData = new FormData();
        formData.append("provider_id", provider_id);
        formData.append("key_alias", this.formData.key_alias);
        formData.append("password", this.formData.password);
        formData.append("key_file", this.formData.key_file);

        const res = await providerNcaKeyStoreAPI.uploadKey(formData);

        if (res?.data) {
          this.showSucceedSnackbar("Ключ успешно загружен и зашифрован");
          if (this.data?.onSubmit) {
            await this.data.onSubmit(res.data);
          }
          this.closeModal();
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Ошибка при загрузке ключа";
        this.showErrorSnackbar(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    async loadProviders() {
      try {
        const res = await providerNcaKeyStoreAPI.getProviders();
        this.providers = res?.data || [];
      } catch (error) {
        console.error("Ошибка при загрузке поставщиков:", error);
      }
    },
  },
  mounted() {
    if (!this.providerId) {
      this.loadProviders();
    }
  },
  setup() {
    const { closeModal } = useAppStore();
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar, closeModal };
  },
};
</script>

<style scoped lang="scss">
.form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px 0;

  &__item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: #333;

    .required {
      color: #f44336;
    }
  }
}

.file-input {
  padding: 10px;
  border: 2px dashed #ccc;
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.3s;

  &:hover {
    border-color: #1976d2;
  }
}

.file-selected {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: 13px;
  color: #333;
}

.info-block {
  display: flex;
  gap: 12px;
  padding: 12px;
  background-color: #e3f2fd;
  border-left: 4px solid #1976d2;
  border-radius: 4px;

  .info-text {
    flex: 1;

    p {
      margin: 0 0 8px 0;
      font-size: 13px;
      color: #555;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
